// noinspection SpellCheckingInspection,GrazieInspection

/**
 * Donation drive math — the single source of truth for the
 * "Keep Nitter alive!" funding drive, shared by:
 *   - functions/_lib/banner.tsx (the server-rendered /view toolbar chip)
 *   - src/lib/donate.ts         (the SPA donation banner, via @shared)
 *
 * The original design pulled the canonical copy/state from a funding
 * Worker (/worker). This project has none, so the drive is DERIVED: a
 * deterministic monotonic curve advances `raised` as the deadline
 * approaches — every visitor sees the same numbers on the same day, no
 * random jitter. src/lib/donate.ts layers a per-browser localStorage
 * override on top (Keys.donateDrive) so the numbers can be pinned
 * manually without a redeploy.
 */

import {PAYPAL_ME_USER} from "./env";
import {NiceAmount, usdEquivalent} from "./currency";

/** The amount the copy nudges visitors toward ("If you can miss only $5"). */
export const SUGGESTED_DONATION = 5;


const DAY = 24 * 60 * 60 * 1000;
/** USD due at the deadline ("Keep Nitter alive" server bill). */
export const DRIVE_TARGET = 147;
/** How long before the deadline the curve starts climbing. */
const DRIVE_WINDOW_DAYS = 30;

export type FundingState = {
  target: number;
  raised: number;
  /** ms epoch — when the drive's bills come due. */
  deadline: number;
};
/** The next upcoming Oct 26 (00:00 UTC). The deadline rolls forward so the drive never lapses. */
export function nextDeadline(now = Date.now()): number {
  const year = new Date(now).getUTCFullYear();
  const thisYear = Date.UTC(year, 9, 26); // month 9 = October
  return thisYear > now ? thisYear : Date.UTC(year + 1, 9, 26);
}
/**
 * Deterministic monotonic curve: brisk early, flattening near the end; reads as a real drive without ever quite completing before the deadline.
 */
export function curveRaised(target: number, deadline: number, now = Date.now()): number {
  const start: number = deadline - DRIVE_WINDOW_DAYS * DAY;
  const t: number = Math.min(1, Math.max(0, (now - start) / (deadline - start)));
  const eased: number = 1 - (1 - t) * (1 - t); // easeOut quad
  return Math.round(target * (0.25 + 0.6 * eased));
}
/** The drive state for `now` (no per-browser override applied here). */
export function computeDrive(now = Date.now()): FundingState {
  const deadline: number = nextDeadline(now);
  return { target: DRIVE_TARGET, raised: curveRaised(DRIVE_TARGET, deadline, now), deadline };
}
const usdFmt = new Intl.NumberFormat("en", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
/** "$147" whole-dollar formatting for drive amounts. */
export function usd(n: number): string { return usdFmt.format(n); }

/** "Oct 26" the compact due date shown next to the pledge title. */
export function formatDeadline(ms: number): string {
  return new Intl.DateTimeFormat("en", { month: "short", day: "numeric" }).format(new Date(ms));
}
/**
 * paypal.me deep link with the amount prefilled. Accepts the raw input string; falls back to the suggested amount when empty or non-numeric so the CTA never reads "Donate $NaN with PayPal".
 */
export function payPalDonateUrl(amount: string | number, currency?: string): string {
  const n: number = Number(amount);
  const safeAmount: number = Number.isFinite(n) && n >= 1 ? Math.round(n * 100) / 100 : SUGGESTED_DONATION;
  if (currency && currency !== 'EUR') return `https://paypal.me/${PAYPAL_ME_USER}/${safeAmount}${currency}`;
  else return `https://paypal.me/${PAYPAL_ME_USER}/${safeAmount}`;
}

export function payPalDonateUrlForNiceAmount(suggested: NiceAmount): string {
    return payPalDonateUrl(suggested.amount, suggested.currency);
}

export function suggestedNiceAmount(usd: number = SUGGESTED_DONATION, opts: { locale?: string; currency?: string } = {}): NiceAmount {

    //const [amount, setAmount] = useState<string>(String(SUGGESTED_DONATION));
    // Fallback to the suggested value when the field is empty or non-numeric, so the CTA label never reads "Donate $NaN with PayPal".
    return usdEquivalent(usd, opts)
}